import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Icon from './Icon';

interface GalleryProps {
  images: {
    src: string;
    alt: string;
    category?: string;
  }[];
  categories?: string[];
  initialCategory?: string | null;
}

const Gallery: React.FC<GalleryProps> = ({ images, categories = [], initialCategory = null }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(initialCategory);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Filtrer les images par catégorie
  const filteredImages = activeCategory
    ? images.filter(image => image.category === activeCategory)
    : images;

  // Ouvrir la lightbox
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Empêcher le défilement
  };

  // Fermer la lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto'; // Réactiver le défilement
  };

  // Image suivante
  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((currentImageIndex + 1) % filteredImages.length);
  };

  // Image précédente
  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((currentImageIndex - 1 + filteredImages.length) % filteredImages.length);
  };

  return (
    <GalleryContainer>
      {categories.length > 0 && (
        <GalleryCategories>
          <CategoryButton 
            active={activeCategory === null}
            onClick={() => setActiveCategory(null)}
          >
            Tout
          </CategoryButton>
          {categories.map(category => (
            <CategoryButton
              key={category}
              active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </CategoryButton>
          ))}
        </GalleryCategories>
      )}

      <GalleryGrid>
        {filteredImages.map((image, index) => (
          <GalleryItem key={index} onClick={() => openLightbox(index)}>
            <GalleryImage src={image.src} alt={image.alt} />
            <GalleryOverlay>
              <GalleryImageTitle>{image.alt}</GalleryImageTitle>
            </GalleryOverlay>
          </GalleryItem>
        ))}
      </GalleryGrid>

      {lightboxOpen && (
        <Lightbox onClick={closeLightbox}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <LightboxImage 
              src={filteredImages[currentImageIndex].src} 
              alt={filteredImages[currentImageIndex].alt} 
            />
            <LightboxCaption>
              {filteredImages[currentImageIndex].alt}
            </LightboxCaption>
            <LightboxClose onClick={closeLightbox}>
              <Icon component={FaTimes} />
            </LightboxClose>
            <LightboxNav>
              <LightboxNavButton onClick={prevImage}>
                <Icon component={FaChevronLeft} />
              </LightboxNavButton>
              <LightboxNavButton onClick={nextImage}>
                <Icon component={FaChevronRight} />
              </LightboxNavButton>
            </LightboxNav>
          </LightboxContent>
        </Lightbox>
      )}
    </GalleryContainer>
  );
};

const GalleryContainer = styled.div`
  padding: 2rem 0;
`;

const GalleryCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
`;

interface CategoryButtonProps {
  active: boolean;
}

const CategoryButton = styled.button<CategoryButtonProps>`
  padding: 1rem 2rem;
  background-color: ${({ active }) => active ? 'var(--color-accent)' : 'transparent'};
  color: ${({ active }) => active ? 'white' : 'var(--color-primary)'};
  border: 1px solid ${({ active }) => active ? 'var(--color-accent)' : 'rgba(0, 0, 0, 0.1)'};
  font-size: 1.4rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background-color: ${({ active }) => active ? 'var(--color-accent)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.div`
  position: relative;
  height: 25rem;
  overflow: hidden;
  cursor: pointer;
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:hover div {
    opacity: 1;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const GalleryOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.7));
  display: flex;
  align-items: flex-end;
  padding: 2rem;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const GalleryImageTitle = styled.h4`
  color: white;
  font-size: 1.8rem;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const Lightbox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
`;

const LightboxCaption = styled.div`
  color: white;
  text-align: center;
  padding: 1rem 0;
  font-size: 1.6rem;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: -4rem;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2.4rem;
  cursor: pointer;
  
  &:hover {
    color: var(--color-accent);
  }
`;

const LightboxNav = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
`;

const LightboxNavButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 3rem;
  cursor: pointer;
  padding: 1rem;
  margin: 0 -5rem;
  
  &:hover {
    color: var(--color-accent);
  }
`;

export default Gallery;
